import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

export function ThankYou() {
  const [searchParams] = useSearchParams();
  const order = searchParams.get('order');
  const [purchase, setPurchase] = useState(null);
  const [status, setStatus] = useState(order ? 'verifying' : 'missing');
  const [message, setMessage] = useState('');
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    if (!order) return undefined;

    let cancelled = false;
    let timeoutId;
    let attempts = 0;

    const verifyPurchase = async () => {
      attempts += 1;
      try {
        const response = await fetch(`/api/pattern-purchase?order=${encodeURIComponent(order)}`, {
          cache: 'no-store',
        });
        const data = await response.json();
        if (cancelled) return;

        if (response.status === 202) {
          if (attempts < 20) {
            timeoutId = window.setTimeout(verifyPurchase, 1500);
            return;
          }
          throw new Error('Your payment is confirmed, but the download is taking longer than expected. Please use the link in your receipt again in a moment.');
        }
        if (!response.ok) throw new Error(data.error || 'We could not verify this purchase.');

        setPurchase(data.pattern);
        setStatus('ready');
      } catch (error) {
        if (!cancelled) {
          setMessage(error.message || 'We could not verify this purchase.');
          setStatus('error');
        }
      }
    };

    verifyPurchase();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [order]);

  const downloadPattern = async (language) => {
    setDownloading(language);
    setMessage('');
    try {
      const response = await fetch('/api/download-pattern', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order, language }),
      });
      const data = await response.json();
      if (!response.ok || !data.url) throw new Error(data.error || 'The download is unavailable.');
      window.location.assign(data.url);
    } catch (error) {
      setMessage(error.message || 'The download is unavailable.');
    } finally {
      setDownloading('');
    }
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mx-auto max-w-xl rounded-2xl border border-green-200 bg-green-50 p-8 text-center">
        <p className="mb-3 text-4xl" aria-hidden="true">✓</p>
        <h1 className="mb-4 text-3xl font-bold text-stone-900">Thank you for your purchase!</h1>
        {status === 'verifying' && (
          <p className="mb-8 text-stone-700" role="status">Preparing your secure download…</p>
        )}
        {status === 'ready' && (
          <div className="mb-8">
            <p className="mb-4 text-stone-700">Your pattern <strong>{purchase.title}</strong> is ready.</p>
            <div className="flex flex-wrap justify-center gap-3">
              {purchase.languages.map((language) => (
                <button
                  key={language}
                  type="button"
                  onClick={() => downloadPattern(language)}
                  disabled={Boolean(downloading)}
                  className="rounded-lg bg-indigo-600 px-5 py-3 font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60"
                >
                  {downloading === language ? 'Preparing…' : `Download · ${language}`}
                </button>
              ))}
            </div>
          </div>
        )}
        {status === 'missing' && (
          <p className="mb-8 text-stone-700">Open the secure link from your Lemon Squeezy receipt to download your pattern.</p>
        )}
        {status === 'error' && (
          <p className="mb-8 text-red-700" role="alert">{message}</p>
        )}
        {message && status !== 'error' && <p className="mb-4 text-red-700" role="alert">{message}</p>}
        <Link
          to="/shop"
          className="inline-flex rounded-lg bg-indigo-600 px-6 py-3 font-medium text-white transition-colors hover:bg-indigo-700"
        >
          Return to the pattern shop
        </Link>
      </div>
    </div>
  );
}

export default ThankYou;
